interface PanelRoot extends HTMLElement {
    _cmsDesignVariablesPanelReady?: boolean;
}

interface WindowRoot extends HTMLElement {
    _cmsDesignVariablesWindowReady?: boolean;
}

const COLOR_VALUE = /^(#[0-9a-f]{3}(?:[0-9a-f]{3})?|rgba?\([^)]+\)|[a-z][a-z0-9-]{0,40})$/i;

function safeCode(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9_.-]+/g, "-")
        .replace(/^[.-]+|[.-]+$/g, "")
        .slice(0, 80);
}

class CmsDesignVariablesPanel {
    #activeCollection = "";
    #query = "";

    constructor(private readonly root: PanelRoot) {
        this.#activeCollection = root.dataset.cmsVariableActive ?? "";
    }

    init(): void {
        if (this.root._cmsDesignVariablesPanelReady) return;
        this.root._cmsDesignVariablesPanelReady = true;

        this.root.addEventListener("click", (event) => this.#onClick(event));
        this.root.addEventListener("input", (event) => this.#onInput(event));
        this.root.addEventListener("submit", (event) => this.#onSubmit(event));
        this.root.querySelectorAll<HTMLFormElement>("[data-cms-variable-form]").forEach((form) => {
            this.#syncValueJson(form);
        });
        this.#refresh();
    }

    #onClick(event: MouseEvent): void {
        const target = event.target as HTMLElement | null;
        const createOpen = target?.closest<HTMLElement>("[data-cms-variable-create-open]");
        if (createOpen && this.root.contains(createOpen)) {
            event.preventDefault();
            const trigger = createOpen.closest<HTMLElement>("[data-cms-variable-create-trigger]");
            const collectionId = trigger?.dataset.collectionId ?? "";
            if (collectionId !== "") {
                this.#activeCollection = collectionId;
                this.root.dataset.cmsVariableActive = collectionId;
            }
            this.root.querySelectorAll<HTMLElement>("[data-cms-variable-create]").forEach((row) => {
                if (row.dataset.collectionId === collectionId) {
                    row.dataset.open = "1";
                }
            });
            this.#refresh();
            this.root
                .querySelector<HTMLElement>(`[data-cms-variable-create][data-collection-id="${collectionId}"] [name="name"]`)
                ?.focus();
            return;
        }

        const collection = target?.closest<HTMLElement>("[data-cms-variable-collection]");
        if (!collection || !this.root.contains(collection)) return;
        event.preventDefault();
        this.#activeCollection = collection.dataset.cmsVariableCollection ?? "";
        this.root.dataset.cmsVariableActive = this.#activeCollection;
        this.#refresh();
    }

    #onInput(event: Event): void {
        const target = event.target as HTMLElement | null;
        if (!target || !this.root.contains(target)) return;

        if (target.matches("[data-cms-variable-search]")) {
            this.#query = (target as HTMLInputElement).value.trim().toLowerCase();
            this.#refresh();
            return;
        }

        if (target.matches("[data-cms-variable-value]")) {
            const form = target.closest<HTMLFormElement>("[data-cms-variable-form]");
            if (form) this.#syncValueJson(form);
            return;
        }

        if (target.matches("[name='name']")) {
            const form = target.closest<HTMLFormElement>(".admin-cms-variable-panel__collection-form");
            const code = form?.querySelector<HTMLInputElement>("[data-cms-variable-collection-code]");
            if (code && code.value.trim() === "") {
                code.value = safeCode((target as HTMLInputElement).value);
            }
        }
    }

    #onSubmit(event: SubmitEvent): void {
        const form = (event.target as HTMLElement | null)?.closest<HTMLFormElement>(".admin-cms-variable-panel__collection-form");
        if (!form || !this.root.contains(form)) return;
        const name = form.querySelector<HTMLInputElement>("[name='name']");
        const code = form.querySelector<HTMLInputElement>("[data-cms-variable-collection-code]");
        if (name && code && code.value.trim() === "") {
            code.value = safeCode(name.value);
        }
    }

    #refresh(): void {
        this.root.querySelectorAll<HTMLElement>("[data-cms-variable-collection]").forEach((button) => {
            const active = (button.dataset.cmsVariableCollection ?? "") === this.#activeCollection;
            button.classList.toggle("is-active", active);
            button.setAttribute("aria-pressed", String(active));
            if (active) {
                button.setAttribute("aria-current", "true");
            } else {
                button.removeAttribute("aria-current");
            }
        });

        let visibleRows = 0;
        this.root.querySelectorAll<HTMLElement>("[data-cms-variable-row]").forEach((row) => {
            const collectionMatch = this.#activeCollection === "" || row.dataset.collectionId === this.#activeCollection;
            const searchMatch = this.#query === "" || (row.dataset.search ?? "").includes(this.#query);
            const visible = collectionMatch && searchMatch;
            row.classList.toggle("is-hidden", !visible);
            if (visible) visibleRows += 1;
        });

        const openCreateCollections = new Set<string>();
        let visibleCreateForms = 0;
        this.root.querySelectorAll<HTMLElement>("[data-cms-variable-create]").forEach((row) => {
            const collectionId = row.dataset.collectionId ?? "";
            const collectionMatch = this.#activeCollection === "" || collectionId === this.#activeCollection;
            const open = row.dataset.open === "1";
            if (open) openCreateCollections.add(collectionId);
            const visible = collectionMatch && open && this.#query === "";
            row.classList.toggle("is-hidden", !visible);
            row.hidden = !visible;
            if (visible) visibleCreateForms += 1;
        });

        let visibleCreateTriggers = 0;
        this.root.querySelectorAll<HTMLElement>("[data-cms-variable-create-trigger]").forEach((trigger) => {
            const collectionId = trigger.dataset.collectionId ?? "";
            const collectionMatch = this.#activeCollection === "" || collectionId === this.#activeCollection;
            const visible = collectionMatch && !openCreateCollections.has(collectionId) && this.#query === "";
            trigger.classList.toggle("is-hidden", !visible);
            trigger.hidden = !visible;
            if (visible) visibleCreateTriggers += 1;
        });

        const empty = this.root.querySelector<HTMLElement>("[data-cms-variable-empty]");
        if (empty) empty.hidden = visibleRows > 0 || visibleCreateForms > 0 || visibleCreateTriggers > 0;
    }

    #syncValueJson(form: HTMLFormElement): void {
        const valueInput = form.querySelector<HTMLInputElement>("[data-cms-variable-value]");
        const jsonInput = form.querySelector<HTMLInputElement>("[data-cms-variable-json]");
        if (!valueInput || !jsonInput) return;

        const value = valueInput.value.trim();
        jsonInput.value = JSON.stringify({ value });

        const swatch = form.querySelector<HTMLElement>("[data-cms-variable-swatch]");
        if (!swatch) return;
        if (COLOR_VALUE.test(value)) {
            swatch.style.setProperty("--cms-variable-swatch", value);
            swatch.classList.remove("is-empty");
            swatch.setAttribute("aria-label", `색상 미리보기 ${value}`);
        } else {
            swatch.style.removeProperty("--cms-variable-swatch");
            swatch.classList.add("is-empty");
            swatch.setAttribute("aria-label", "색상 값이 유효하지 않습니다");
        }
    }
}

class CmsDesignVariablesWindow {
    #dragging = false;
    #startX = 0;
    #startY = 0;
    #originLeft = 0;
    #originTop = 0;

    constructor(private readonly root: WindowRoot) {}

    init(): void {
        if (this.root._cmsDesignVariablesWindowReady) return;
        const open = this.root.querySelector<HTMLElement>("[data-cms-design-variables-open]");
        const windowEl = this.root.querySelector<HTMLElement>("[data-cms-design-variables-window]");
        if (!open || !windowEl) return;
        this.root._cmsDesignVariablesWindowReady = true;

        open.addEventListener("click", () => {
            windowEl.hidden = false;
            windowEl.classList.add("is-open");
            this.#ensurePosition(windowEl);
        });

        windowEl.querySelectorAll<HTMLElement>("[data-cms-variable-window-close]").forEach((button) => {
            button.addEventListener("click", () => {
                windowEl.classList.remove("is-open");
                windowEl.hidden = true;
            });
        });

        const handle = windowEl.querySelector<HTMLElement>("[data-cms-design-variables-drag-handle]");
        handle?.addEventListener("pointerdown", (event) => this.#startDrag(event, windowEl));
        window.addEventListener("pointermove", (event) => this.#move(event, windowEl));
        window.addEventListener("pointerup", () => this.#stopDrag());
    }

    #ensurePosition(windowEl: HTMLElement): void {
        if (windowEl.style.left !== "" || windowEl.style.top !== "") return;
        const width = Math.min(980, window.innerWidth - 48);
        windowEl.style.left = `${Math.max(24, (window.innerWidth - width) / 2)}px`;
        windowEl.style.top = `${Math.max(72, window.innerHeight * 0.12)}px`;
    }

    #startDrag(event: PointerEvent, windowEl: HTMLElement): void {
        const target = event.target as HTMLElement | null;
        if (target?.closest("button, input, select, textarea, a")) return;
        this.#ensurePosition(windowEl);
        this.#dragging = true;
        this.#startX = event.clientX;
        this.#startY = event.clientY;
        this.#originLeft = parseFloat(windowEl.style.left || "0");
        this.#originTop = parseFloat(windowEl.style.top || "0");
        windowEl.classList.add("is-dragging");
        try {
            windowEl.setPointerCapture?.(event.pointerId);
        } catch {
            /* Pointer capture is an enhancement; dragging still works via window listeners. */
        }
    }

    #move(event: PointerEvent, windowEl: HTMLElement): void {
        if (!this.#dragging || windowEl.hidden) return;
        const maxLeft = Math.max(0, window.innerWidth - windowEl.offsetWidth);
        const maxTop = Math.max(0, window.innerHeight - windowEl.offsetHeight);
        const nextLeft = Math.min(maxLeft, Math.max(0, this.#originLeft + event.clientX - this.#startX));
        const nextTop = Math.min(maxTop, Math.max(0, this.#originTop + event.clientY - this.#startY));
        windowEl.style.left = `${nextLeft}px`;
        windowEl.style.top = `${nextTop}px`;
    }

    #stopDrag(): void {
        if (!this.#dragging) return;
        this.#dragging = false;
        this.root.querySelector<HTMLElement>("[data-cms-design-variables-window]")?.classList.remove("is-dragging");
    }
}

export function initCmsDesignVariablesPanel(): void {
    document.querySelectorAll<PanelRoot>("[data-cms-design-variables-panel]").forEach((root) => {
        new CmsDesignVariablesPanel(root).init();
    });

    document.querySelectorAll<WindowRoot>("[data-cms-builder]").forEach((root) => {
        new CmsDesignVariablesWindow(root).init();
    });
}
